import { useState, useCallback } from "react";
import { ToolContainer, FileDropZone, downloadBlob } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import * as XLSX from "xlsx";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { decodeCsvBytes, detectDelimiter, parseDelimited, safeCsvText, toCsvText, typedCell } from "./csvParse";
import { Download } from "lucide-react";

const TOOL_INFO_CONTENT = {
  what: `Converts between CSV/TSV and Excel (.xlsx), in both directions, entirely in your browser: Excel to CSV, and CSV (comma, semicolon, tab or pipe separated) to a real Excel file.`,
  how: `Drop a CSV, TSV, or Excel file. For a workbook with several sheets, pick the sheet to convert. Then download as CSV (formula-looking cells are escaped by default; optionally with a BOM so Excel shows accented letters correctly) or as an Excel file. The delimiter and text encoding of CSV files are detected automatically and shown, so you can check them.`,
  tip: `CSV is one flat table with no formatting or formulas, so converting Excel to CSV drops those. Plain numbers in a CSV become real numbers in the Excel file; values like 00123 or very long digit strings stay as text so nothing is changed or rounded.`,
};

const DELIMS: Record<string, string> = { ",": "comma", ";": "semicolon", "\t": "tab", "|": "pipe" };

export default function CsvConverter() {
  const [file, setFile] = useState<File | null>(null);
  const [data, setData] = useState<string[][]>([]);
  const [info, setInfo] = useState("");
  const [wb, setWb] = useState<XLSX.WorkBook | null>(null);
  const [sheets, setSheets] = useState<string[]>([]);
  const [sheet, setSheet] = useState("");
  const [bom, setBom] = useState(true);
  const [escapeFormulas, setEscapeFormulas] = useState(true);
  const [delimiter, setDelimiter] = useState(",");

  const loadSheet = (book: XLSX.WorkBook, name: string) => {
    const rows = XLSX.utils.sheet_to_json<unknown[]>(book.Sheets[name], { header: 1, raw: false, defval: "" });
    setData(rows.map(r => r.map(c => String(c ?? ""))));
    setSheet(name);
  };

  const handleFiles = useCallback(async (files: File[]) => {
    const f = files[0]; if (!f) return;
    setFile(f); setWb(null); setSheets([]); setInfo("");
    const ext = f.name.match(/\.([^.]+)$/)?.[1]?.toLowerCase();
    try {
      const buffer = await f.arrayBuffer();
      if (ext === "xlsx" || ext === "xls" || ext === "xlsm" || ext === "ods") {
        const book = XLSX.read(buffer, { type: "array" });
        const visible = book.SheetNames.filter((_, i) => !book.Workbook?.Sheets?.[i]?.Hidden);
        const names = visible.length ? visible : book.SheetNames;
        setWb(book); setSheets(names); loadSheet(book, names[0]);
        setInfo(names.length > 1 ? `${names.length} sheets` : "");
      } else {
        const { text, encoding } = decodeCsvBytes(buffer);
        const d = ext === "tsv" ? "\t" : detectDelimiter(text);
        setDelimiter(d);
        setData(parseDelimited(text, d));
        setSheet("");
        setInfo(`Detected: ${DELIMS[d]}-separated, ${encoding}`);
      }
    } catch (err) {
      console.error("Failed to parse file:", err);
      toast.error("Couldn't read that file — make sure it's a valid CSV, TSV, or Excel file.");
      setFile(null);
    }
  }, []);

  const base = file ? file.name.replace(/\.[^.]+$/, "") : "converted";
  const downloadCsv = () => {
    downloadBlob(new Blob([escapeFormulas ? safeCsvText(data, ",", bom) : toCsvText(data, ",", bom)], { type: "text/csv;charset=utf-8" }), `${base}${sheet && sheets.length > 1 ? `-${sheet.replace(/[\\/:*?"<>|]/g, "_")}` : ""}.csv`);
  };
  const downloadXlsx = () => {
    const ws = XLSX.utils.aoa_to_sheet(data.map(r => r.map(typedCell)));
    const book = XLSX.utils.book_new();
    const name = (sheet || "Sheet1").replace(/[\\/?*[\]:]/g, "_").slice(0, 31) || "Sheet1";
    XLSX.utils.book_append_sheet(book, ws, name);
    const out = XLSX.write(book, { type: "array", bookType: "xlsx" });
    downloadBlob(new Blob([out], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), `${base}.xlsx`);
  };

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept=".csv,.tsv,.txt,.xlsx,.xls,.xlsm,.ods" onFiles={handleFiles} label="Drop a CSV or Excel file" description="Excel → CSV or CSV → Excel" />
      ) : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card">
            <p className="text-sm font-medium">{file.name} — {data.length} rows{data[0] ? `, ${data[0].length} columns` : ""}</p>
            {info && <p className="text-xs text-muted-foreground mt-1">{info}</p>}
            {sheets.length > 1 && wb && (
              <label className="text-sm mt-2 flex items-center gap-2">Sheet:
                <select aria-label="Sheet to convert" className="border rounded px-1 bg-background" value={sheet} onChange={e => loadSheet(wb, e.target.value)}>
                  {sheets.map(s => <option key={s}>{s}</option>)}
                </select>
              </label>
            )}
          </div>
          {data.length > 0 && (
            <div className="border rounded-lg overflow-auto max-h-64">
              <table className="w-full text-xs">
                <tbody>{data.slice(0, 20).map((row, i) => (
                  <tr key={i} className={i === 0 ? "bg-muted font-medium" : "border-t"}>
                    {row.map((cell, j) => <td key={j} className="p-2 whitespace-nowrap">{cell}</td>)}
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
          {data.length > 20 && <p className="text-xs text-muted-foreground">Showing the first 20 of {data.length} rows.</p>}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={bom} onChange={e => setBom(e.target.checked)} /> Add a BOM to the CSV (so Excel shows é, ü, ₹ and other letters correctly)</label>
          <div>
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={escapeFormulas} onChange={e => setEscapeFormulas(e.target.checked)} /> Protect against formula injection in the CSV (adds a leading ' to cells starting with =, +, - or @)</label>
            {!escapeFormulas && <p className="text-xs text-muted-foreground mt-1 ml-6">Off: cells such as =1+1 will run as formulas when the CSV is opened in Excel or Sheets. Only turn this off for data you trust.</p>}
          </div>
          <div className="flex flex-wrap gap-3">
            <Button onClick={downloadCsv} className="gap-2"><Download className="h-4 w-4" /> Download CSV</Button>
            <Button onClick={downloadXlsx} variant="outline" className="gap-2"><Download className="h-4 w-4" /> Download Excel (.xlsx)</Button>
            <Button variant="ghost" onClick={() => { setFile(null); setData([]); setSheets([]); setWb(null); setInfo(""); }}>New File</Button>
          </div>
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
